import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'
import Button from '@carsys/ui/src/Button'
import { Form, SubmitButton, TextField } from '@carsys/form'
import { IShopWholesaler, OrderAccountCredentials } from '@cbgms/api/modules'
import { PERMISSION_ORDERACCOUNT_HIDE } from 'utils/Subscription/Permissions'
import { useHasPermission } from 'utils/Subscription/RequiredPermissions'
import { WholesalerCredentialFields } from 'app/accounts/components/SetupWholesalers/WholesalerCredentialFields'

interface Props {
  selectedWholesaler: IShopWholesaler
  initialAccountUUID: string
  initialOrderAccountUUID?: string
  initialCredentials?: OrderAccountCredentials
  initialDoNotShowOnList?: boolean
  forceHideDoNotShowOnList?: boolean
  handleOnSubmit: (values: FormProps) => Promise<FormProps>
  handleOnSubmitSuccess: (values: FormProps) => Promise<void>
  handleBack: () => void
  handleClose: () => void
  addOrderAccountButtonSlot?: React.ReactNode
}

export interface FormProps {
  WholesalerUUID: string
  AccountUUID: string
  Credentials: OrderAccountCredentials
  CompanyName: string
  DoNotShowOnList: boolean
  OrderAccountUUID?: string // not set when creating new account
  wholesaler: IShopWholesaler
}

export function OrderAccountCredentialsDialogContent({
  initialAccountUUID,
  initialOrderAccountUUID,
  initialDoNotShowOnList,
  forceHideDoNotShowOnList,
  initialCredentials,
  selectedWholesaler,
  handleBack,
  handleOnSubmit,
  handleOnSubmitSuccess,
  addOrderAccountButtonSlot,
  handleClose
}: Props) {
  const { t } = useTranslation()
  const [canHideOrderAccount] = useHasPermission(PERMISSION_ORDERACCOUNT_HIDE)
  const initialCredentialValues = useMemo(() => {
    return (
      (initialCredentials && { ...initialCredentials, CurrentPassword: initialCredentials?.Password, Password: '' }) ||
      selectedWholesaler?.UserFields.reduce((prev, curr) => Object.assign(prev, { [curr.Name]: '' }), {} as OrderAccountCredentials)
    )
  }, [selectedWholesaler, selectedWholesaler])

  const hideCompanyNameField = !initialAccountUUID
  const isEditFlow = !!initialCredentials

  return (
    <>
      <DialogTitle>{`${t('Configuration.FillInCredentialsWholesaler')} ${selectedWholesaler.Name}`}</DialogTitle>
      <Form
        initialValues={{
          OrderAccountUUID: initialOrderAccountUUID,
          AccountUUID: initialAccountUUID,
          Credentials: initialCredentialValues!,
          CompanyName: '',
          DoNotShowOnList: !!initialDoNotShowOnList,
          WholesalerUUID: selectedWholesaler.UUID,
          wholesaler: selectedWholesaler
        }}
        NavigationConfirmation={null}
        onSubmit={handleOnSubmit}
        onSubmitSuccess={handleOnSubmitSuccess}
        enableReinitialize
        validationSchema={Yup.object().shape({
          // Credentials will be using field level validation
          CompanyName: Yup.string().when('AccountUUID', {
            is: (value: string) => !value,
            then: schema => schema.required()
          })
        })}
      >
        <DialogContent>
          <WholesalerCredentialFields
            wholesaler={selectedWholesaler}
            hideDoNotShowOnListOptions={!canHideOrderAccount || !!forceHideDoNotShowOnList}
          >
            {hideCompanyNameField && <TextField required type='text' name='CompanyName' label={t('Account.CompanyName')} />}
          </WholesalerCredentialFields>
        </DialogContent>
        <DialogActions data-testid='order-account-credentials-actions'>
          {addOrderAccountButtonSlot || (
            <Stack direction='row' spacing={1}>
              {!isEditFlow && (
                <Button onClick={handleBack} variant='text'>
                  {t('global.cancel')}
                </Button>
              )}
              {isEditFlow && (
                <Button onClick={handleClose} variant='text'>
                  {t('global.close')}
                </Button>
              )}
              <SubmitButton />
            </Stack>
          )}
        </DialogActions>
      </Form>
    </>
  )
}
